import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ExamSteps from './ExamSteps';
import ExamQuestions from './ExamQuestions';
import { 
  PlusIcon, 
  PhotographIcon, 
  TrashIcon,
  EyeIcon,
  EyeOffIcon,
  QuestionMarkCircleIcon,
  ClockIcon,
  CalendarIcon,
  SaveAsIcon,
  CloudUploadIcon
} from '@heroicons/react/outline';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { toastService } from '../../../../../services/toast';
import { coursesApi } from '../../../../../services/api/courses.api';
import { examsService } from '../../../../../services/exams';
import axios from 'axios';

// بيانات افتراضية للحقل
const defaultFormData = {
  title: '',
  description: '',
  course_id: '',
  duration: 0,
  passing_score: 0,
  status: 'draft',
  show_results: true,
  attempts_allowed: 0,
  questions: [],
  total_score: 0,
  totalScore: 0,
  skippable: false,
  isFree: false, // Added isFree with default false
};

const CreateExamPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalAction, setModalAction] = useState(''); // 'publish' or 'draft'
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState(defaultFormData);
  
  // إضافة متغير لتخزين معرف الاختبار بعد إنشائه
  const [examId, setExamId] = useState(null);
  
  // متغير لتتبع حالة حفظ الأسئلة
  const [isSavingQuestion, setIsSavingQuestion] = useState(false);
  
  // دالة لجلب بيانات الاختبار
  const fetchExamData = async (examId) => {
    setIsLoading(true);
    try {
      // جلب بيانات الاختبار
      const examData = await coursesApi.getExamById(examId);
      // تم استلام بيانات الاختبار
      setFormData(examData);
      setExamId(examId);
    } catch (error) {
      // حدث خطأ أثناء جلب بيانات الاختبار
      toastService.error('حدث خطأ أثناء جلب بيانات الاختبار');
    } finally {
      setIsLoading(false);
    }
  };
  
  // جلب قائمة الدورات عند تحميل الصفحة
  useEffect(() => {
    const fetchCourses = async () => {
      setIsLoading(true);
      try {
        const coursesData = await coursesApi.getCoursesForExam();
        setCourses(coursesData);
      } catch (error) {
        // حدث خطأ أثناء جلب قائمة الدورات
        toastService.error('حدث خطأ أثناء جلب قائمة الدورات');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();

    // قراءة القيم من localStorage
    const savedExamId = localStorage.getItem('currentExamId');
    const savedStep = localStorage.getItem('currentExamStep');
    
    // التحقق من البيانات المحفوظة
    // التحقق من الصفحة السابقة

    if (params.id) {
      // نحن في وضع تعديل اختبار موجود عبر URL
      setExamId(params.id);
      fetchExamData(params.id);
      return; // لا حاجة للاستمرار
    }

    if (savedExamId && savedStep) {
      // استعادة جلسة إنشاء اختبار غير مكتملة بعد عمل ريفرش
      setExamId(savedExamId);
      const stepInt = parseInt(savedStep);
      // التأكد من ضبط الخطوة بشكل صحيح
      if (!isNaN(stepInt) && stepInt > 0) {
        // ضبط الخطوة
        setTimeout(() => setCurrentStep(stepInt), 0);
      } else {
        setCurrentStep(1);
      }
      fetchExamData(savedExamId);
    } else {
      // تم المسح مسبقاً إن لزم الأمر، نبدأ من الصفر
      setCurrentStep(1);
      setExamId(null);
      setFormData(defaultFormData);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggleChange = (name) => {
    setFormData(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // دالة لإضافة سؤال جديد
  const handleAddQuestion = async (newQuestion) => {
    // إضافة مؤقتة مع حالة تحميل
    const tempId = Date.now();
    const order = formData.questions.length + 1;
    const tempQuestion = { ...newQuestion, tempId, order, isSaving: true };

    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, tempQuestion]
    }));

    if (!examId) return; // لا يمكن الحفظ بدون معرف الاختبار

    try {
      const response = await coursesApi.addExamQuestion(examId, { ...newQuestion, order });
      if (response) {
        // بعد نجاح الحفظ نجلب الأسئلة مباشرة
        try {
          const serverQuestions = await coursesApi.getExamQuestions(examId); // تُرجع مصفوفة مباشرة
          if (Array.isArray(serverQuestions)) {
            setFormData(prev => ({ ...prev, questions: serverQuestions.sort((a,b)=>(a.order||0)-(b.order||0)) }));
          }
          return;
        } catch(fetchErr){
          // حدث خطأ أثناء جلب الأسئلة بعد الإضافة
        }
      }
    } catch (error) {
      // حدث خطأ أثناء حفظ السؤال
      toastService.error('حدث خطأ أثناء حفظ السؤال');
      // إزالة السؤال المؤقت
      setFormData(prev => ({
        ...prev,
        questions: prev.questions.filter(q => q.tempId !== tempId)
      }));
    }
  };

  // دالة لحفظ السؤال مباشرة في قاعدة البيانات
  const handleDirectSaveQuestion = async (examId, questionData) => {
    // احتياطي إذا احتجناه من الطفل
    return await coursesApi.addExamQuestion(examId, questionData);
  };

  // دالة لحذف السؤال والإجابات المرتبطة به نهائيًا من قاعدة البيانات
  const handleDeleteQuestion = async (index, questionId) => {
    try {
      // عرض معلومات تشخيصية
      // حذف السؤال
      
      // التحقق من وجود معرف الاختبار
      if (!examId) {
        // معرف الاختبار غير متوفر
        // إذا لم يكن هناك معرف اختبار، نحذف السؤال من المصفوفة المحلية فقط
        const updatedQuestions = formData.questions.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, questions: updatedQuestions }));
        toastService.warning('تم حذف السؤال من القائمة فقط لأن معرف الاختبار غير متوفر');
        return;
      }
      
      // التحقق من وجود السؤال في المصفوفة
      if (index >= 0 && index < formData.questions.length) {
        // الحصول على السؤال المراد حذفه
        const questionToDelete = formData.questions[index];
        // السؤال المراد حذفه
        
        // إذا كان السؤال مؤقت (tempId موجود أو لا يوجد id) نحذفه محلياً دون طلب API
        if (questionToDelete.tempId || !questionToDelete.id) {
          const updatedQuestions = formData.questions.filter((_, i) => i !== index);
          setFormData(prev => ({ ...prev, questions: updatedQuestions }));
          return;
        }
        
        // الحصول على معرف السؤال من السؤال نفسه أو من المعامل الممرر
        let actualQuestionId = null;
        
        // محاولة الحصول على معرف السؤال من مصادر مختلفة
        // طباعة كل الخصائص الموجودة في السؤال للتشخيص
        // خصائص السؤال
        // بيانات السؤال
        
        // محاولة الحصول على معرف السؤال من أي حقل محتمل
        actualQuestionId = questionToDelete.id || questionToDelete.db_id || questionToDelete._id || questionToDelete.question_id || questionId;
        
        // معرف السؤال النهائي
        
        // إذا لم يكن هناك معرف سؤال، نحذف السؤال من المصفوفة المحلية فقط
        if (!actualQuestionId) {
          // لم يتم العثور على معرف السؤال، سيتم الحذف محلياً فقط
          const updatedQuestions = formData.questions.filter((_, i) => i !== index);
          setFormData(prev => ({ ...prev, questions: updatedQuestions }));
          toastService.warning('تم حذف السؤال من القائمة فقط لأن معرف السؤال غير متوفر');
          return;
        }
        
        // استخدام نقطة النهاية الجديدة للحذف النهائي للسؤال وإجاباته
        // تنفيذ الحذف النهائي للسؤال
        
        try {
          // استخدام دالة الحذف النهائي الجديدة التي ستتعامل مع نقطة النهاية الجديدة في الباكند
          const response = await coursesApi.permanentDeleteExamQuestion(examId, actualQuestionId);
          // تم الحذف النهائي بنجاح
          toastService.success('تم حذف السؤال وإجاباته نهائياً بنجاح');
        } catch (apiError) {
          // حدث خطأ أثناء الحذف النهائي للسؤال
          
          // إذا لم يتم تنفيذ نقطة النهاية الجديدة في الباكند، نستخدم الحذف الناعم كاحتياطي
          try {
            // استخدام الحذف الناعم كبديل
            await coursesApi.deleteExamQuestion(examId, actualQuestionId);
            toastService.warning('تم حذف السؤال بشكل ناعم فقط. يرجى تنفيذ نقطة النهاية الجديدة في الباكند للحذف النهائي.');
          } catch (softDeleteError) {
            // حدث خطأ حتى مع الحذف الناعم
            toastService.error('حدث خطأ أثناء حذف السؤال');
            return;
          }
        }
        
        // حذف السؤال محلياً أولاً
        const updatedQuestions = formData.questions.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, questions: updatedQuestions }));

        // تحديث القائمة من السيرفر
        try {
          const response = await coursesApi.getExamQuestions(examId);
          if (Array.isArray(response)) {
            const serverQuestions = response; // مصفوفة حقيقية
            // الأسئلة المحلية التي لم يتم حذفها
            const remainingLocalQuestions = updatedQuestions.filter(q => q.tempId);
            const mergedQuestions = [...serverQuestions, ...remainingLocalQuestions];
            
            // ترتيب الأسئلة حسب الترتيب
            const sortedQuestions = mergedQuestions.sort((a, b) => (a.order || 0) - (b.order || 0));
            
            setFormData(prev => ({
              ...prev,
              questions: sortedQuestions
            }));
          }
        } catch (error) {
          // حدث خطأ أثناء تحديث قائمة الأسئلة
          toastService.warning('تم الحذف بنجاح ولكن فشل تحديث القائمة تلقائياً');
        }
      } else {
        // مؤشر السؤال غير صالح
        toastService.error('مؤشر السؤال غير صالح');
      }
    } catch (error) {
      // حدث خطأ أثناء محاولة حذف السؤال
      toastService.error('حدث خطأ أثناء محاولة حذف السؤال');
    }
  };

  const renderBasicInfo = () => (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="p-6">
          <div className="space-y-6">
            {/* الصف الأول: العنوان والدرجة الكلية */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  عنوان الاختبار
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="mt-1 block w-full h-10 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="أدخل عنوان الاختبار"
                />
              </div>
              <div>
                <label htmlFor="totalScore" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  الدرجة الكلية
                </label>
                <input
                  type="number"
                  id="totalScore"
                  name="totalScore"
                  value={formData.totalScore}
                  onChange={handleInputChange}
                  className="mt-1 block w-full h-10 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="أدخل الدرجة الكلية"
                />
              </div>
            </div>

            {/* الصف الثاني: مدة الاختبار والدورة */}
            <div className="grid grid-cols-2 gap-6 mt-6">
              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  مدة الاختبار (بالدقائق)
                </label>
                <input
                  type="number"
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  className="mt-1 block w-full h-10 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="أدخل مدة الاختبار"
                />
              </div>
              <div>
                <label htmlFor="course_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  الدورة التابع لها
                </label>
                <select
                  id="course_id"
                  name="course_id"
                  value={formData.course_id}
                  onChange={handleInputChange}
                  className="mt-1 block w-full h-10 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                >
                  <option value="">اختر الدورة</option>
                  {courses && courses.length > 0 ? (
                    courses.map(course => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>لا توجد دورات متاحة</option>
                  )}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label htmlFor="passingScore" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                درجة النجاح (اختياري)
              </label>
              <input
                type="number"
                id="passingScore"
                name="passingScore"
                value={formData.passingScore}
                onChange={handleInputChange}
                className="mt-1 block w-full h-10 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                min="0"
                max={formData.totalScore}
                placeholder="اترك فارغاً إذا لم تكن هناك درجة نجاح محددة"
              />
            </div>

            {/* خيارات الاختبار */}
            <div className="space-y-4 mt-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    إعادة الاختبار
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formData.retryLimit ? 'مسموح بإعادة الاختبار مرة واحدة فقط' : 'مسموح بإعادة الاختبار بلا حدود'}
                  </p>
                </div>
                <div className="flex items-center justify-center w-14">
                  <button
                    type="button"
                    onClick={() => handleToggleChange('retryLimit')}
                    className={`${
                      formData.retryLimit ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                    } flex h-6 w-11 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                  >
                    <span
                      aria-hidden="true"
                      className={`${
                        formData.retryLimit ? 'mr-5' : 'mr-0'
                      } inline-block h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ease-in-out`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    تخطي الأسئلة
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {formData.skippable ? 'يمكن للطلاب تخطي الأسئلة والعودة إليها لاحقاً' : 'يجب الإجابة على الأسئلة بالترتيب'}
                  </p>
                </div>
                <div className="flex items-center justify-center w-14">
                  <button
                    type="button"
                    onClick={() => handleToggleChange('skippable')}
                    className={`${
                      formData.skippable ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                    } flex h-6 w-11 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                  >
                    <span
                      aria-hidden="true"
                      className={`${
                        formData.skippable ? 'mr-5' : 'mr-0'
                      } inline-block h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ease-in-out`}
                    />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 mt-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  منشور
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.status === 'published' ? 'الاختبار منشور ومتاح للطلاب' : 'الاختبار مخفي ولا يمكن للطلاب رؤيته'}
                </p>
              </div>
              <div className="flex items-center justify-center w-14">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    status: prev.status === 'published' ? 'draft' : 'published'
                  }))}
                  className={`${
                    formData.status === 'published' ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                  } flex h-6 w-11 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                >
                  <span
                    aria-hidden="true"
                    className={`${
                      formData.status === 'published' ? 'mr-5' : 'mr-0'
                    } inline-block h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 mt-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  مجاني
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.isFree ? 'الاختبار متاح للجميع مجاناً' : 'الاختبار مدفوع ويتطلب شراء الدورة'}
                </p>
              </div>
              <div className="flex items-center justify-center w-14">
                <button
                  type="button"
                  onClick={() => handleToggleChange('isFree')}
                  className={`${
                    formData.isFree ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                  } flex h-6 w-11 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
                >
                  <span
                    aria-hidden="true"
                    className={`${
                      formData.isFree ? 'mr-5' : 'mr-0'
                    } inline-block h-5 w-5 rounded-full bg-white shadow transition-all duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // تم إزالة الإعدادات المتقدمة

  // دالة لإنشاء الاختبار في الخطوة الأولى
  const handleCreateExam = async () => {
    setIsSubmitting(true);
    try {
      // التحقق من البيانات المطلوبة
      if (!formData.title) {
        toastService.error('يرجى إدخال عنوان الاختبار');
        setIsSubmitting(false);
        return false;
      }
      
      // التحقق من اختيار الدورة
      if (!formData.course_id) {
        toastService.error('يرجى اختيار الدورة');
        setIsSubmitting(false);
        return false;
      }

      // إنشاء الاختبار بدون الأسئلة
      const examData = {
        title: formData.title,
        description: formData.description || '',
        course_id: formData.course_id,
        duration: formData.duration,
        passing_score: formData.passingScore || 0,
        status: formData.status,
        show_results: formData.show_results,
        attempts_allowed: formData.retryLimit ? 1 : 0,
        skippable: formData.skippable,
        is_free: formData.isFree || false, // Added is_free to the API request
        total_score: formData.totalScore
      };
      
      // إرسال بيانات الاختبار

      // إرسال بيانات الاختبار إلى API
      const response = await coursesApi.createExam(examData);
      
      // معالجة الاستجابة
      
      // استخراج معرف الاختبار من الاستجابة
      

      
      // استخراج معرف الاختبار من الاستجابة بعد فهم البنية الصحيحة
      let newExamId = null;
      
      // في coursesApi.createExam يتم إرجاع response.data مباشرة
      // لذلك الاستجابة التي نتلقاها هنا هي response وليس response.data
      if (response && response.exam && typeof response.exam === 'object') {
        newExamId = response.exam.id;
      } else {
        // محاولة أخرى في حالة اختلاف بنية الاستجابة
        if (response && typeof response === 'object') {
          // إذا كان هناك مفتاح 'data'
          if ('data' in response) {
            if (response.data && response.data.exam) {
              newExamId = response.data.exam.id;
            }
          }
        }
      }
      
      if (newExamId) {
        setExamId(newExamId);
        // حفظ معرف الاختبار في localStorage لاسترجاعه بعد التحديث
        localStorage.setItem('currentExamId', newExamId);
      } else {
        // إذا لم يتم العثور على معرف الاختبار، نعرض رسالة خطأ
        toastService.error('تم إنشاء الاختبار ولكن لم يتم الحصول على معرفه. يرجى المحاولة مرة أخرى.');
        setIsSubmitting(false);
        return false;
      }
      
      toastService.success('تم إنشاء الاختبار بنجاح');
      setIsSubmitting(false);
      return true;
    } catch (error) {
      // حدث خطأ أثناء إنشاء الاختبار
      toastService.error('حدث خطأ أثناء إنشاء الاختبار');
      setIsSubmitting(false);
      return false;
    }
  };
  
  // دالة لإضافة الأسئلة في الخطوة الثانية
  const handleSaveQuestions = async () => {
    try {
      setIsLoading(true);
      
      // التحقق من وجود معرف الاختبار
      if (!examId) {
        toastService.error('لم يتم إنشاء الاختبار بعد');
        setIsLoading(false);
        return;
      }
      
      // التحقق من وجود أسئلة في الاختبار (إجباري)
      if (!formData.questions || formData.questions.length === 0) {
        toastService.error('يجب إضافة سؤال واحد على الأقل للاختبار');
        setIsLoading(false);
        return;
      }
      
      // إرسال فقط الأسئلة التي لم يُنشأ لها id بعد
      const unsavedQuestions = formData.questions.filter(q => !q.id);

      for (const question of unsavedQuestions) {
        try {
          await coursesApi.addExamQuestion(examId, question);
          // تم إضافة السؤال بنجاح
        } catch (questionError) {
          // حدث خطأ أثناء إضافة السؤال
          // لا نتوقف عند خطأ في سؤال واحد، نستمر في إضافة بقية الأسئلة
        }
      }
      
      // رسالة نجاح
      toastService.success('تم حفظ أسئلة الاختبار بنجاح');
      
      // حذف بيانات الجلسة من localStorage
      localStorage.removeItem('currentExamId');
      localStorage.removeItem('currentExamStep');
      
      // الانتقال إلى صفحة الاختبارات بعد الحفظ
      navigate('/admin/dashboard/courses/exams');
    } catch (error) {
      // حدث خطأ أثناء حفظ الأسئلة
      toastService.error('حدث خطأ أثناء حفظ أسئلة الاختبار');
    } finally {
      setIsLoading(false);
    }
  };
  
  // دالة للانتقال بين الخطوات
  const handleNextStep = async () => {
    if (currentStep === 1) {
      // إذا كنا في الخطوة الأولى، نقوم بإنشاء الاختبار أولاً
      const success = await handleCreateExam();
      if (success) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        // حفظ الخطوة الحالية في localStorage
        localStorage.setItem('currentExamStep', nextStep);
      }
    } else {
      // الانتقال إلى الخطوة التالية مباشرة
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // حفظ الخطوة الحالية في localStorage
      localStorage.setItem('currentExamStep', nextStep);
    }
  };

  // تم إزالة دالة handleConfirm لأن الحفظ يتم مباشرة

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        {/* رأس البطاقة */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h1 className="text-lg font-medium text-gray-900 dark:text-white">إنشاء اختبار جديد</h1>
        </div>

        {/* Stepper */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <ExamSteps currentStep={currentStep} />
        </div>

        {/* محتوى البطاقة */}
        <div className="p-6">
          {currentStep === 1 && renderBasicInfo()}
          {currentStep === 2 && (
            <ExamQuestions
              questions={formData.questions}
              onAddQuestion={handleAddQuestion}
              onUpdateQuestion={(index, updatedQuestion) => {
                const updatedQuestions = [...formData.questions];
                updatedQuestions[index] = updatedQuestion;
                setFormData(prev => ({ ...prev, questions: updatedQuestions }));
              }}
              onDeleteQuestion={handleDeleteQuestion}
              examId={examId}
              onDirectSave={handleDirectSaveQuestion}
              totalScore={100} // يمكن تغييره حسب احتياجك
            />
          )}
        </div>

        {/* أزرار التنقل */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 rounded-b-lg border-t border-gray-200 dark:border-gray-600 flex justify-between items-center">
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors duration-200 min-w-[120px]"
              >
                السابق
              </button>
            )}
          </div>
          
          <div className="flex gap-4">
            {currentStep < 2 ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="px-4 py-2.5 text-sm font-medium text-white rounded-md transition-colors duration-200 min-w-[120px] bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                disabled={isLoading}
              >
                {currentStep === 1 ? 'إنشاء الاختبار والمتابعة' : 'التالي'}
                {isLoading && <span className="mr-2 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSaveQuestions}
                className="px-4 py-2.5 text-sm font-medium text-white rounded-md transition-colors duration-200 min-w-[120px] bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                disabled={isLoading}
              >
                حفظ الأسئلة
                {isLoading && <span className="mr-2 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* تم إزالة نافذة التأكيد */}
    </div>
  );
};

export default CreateExamPage;
